import { useState, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { logError, logEvent } from '@cortex/faro';
import { isMemberOnlyRoleSet } from '@cortex/core-types';
import {
  useAccounts,
  useUserSearch,
  useUpdateAccount,
  useDeleteAccount,
  useAuth,
} from '../../hooks/index.js';
import type { Account, PortalRole, UserSearchResult, UpdateAccountInput } from '../../types/api.js';
import { accountApi, ApiError } from '../../lib/api.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アカウント管理ページ。ポータルユーザーの一覧・検索・ロール更新・削除モーダルを提供。admin/group_manager権限必須
 * @graph-connects useAccounts [calls] アカウント一覧取得フック呼び出し
 * @graph-connects useUserSearch [calls] ユーザー検索フック呼び出し
 * @graph-connects useUpdateAccount [calls] アカウント更新フック呼び出し
 * @graph-connects useDeleteAccount [calls] アカウント削除フック呼び出し
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects isMemberOnlyRoleSet [calls] PMO 制約（対象は member ロールのみ操作可）の判定
 */
export const Route = createFileRoute('/accounts/')({
  component: AccountsPage,
});

/** @graph-connects pilab-api [calls] PILab APIを呼び出す */
function AccountsPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { data, isLoading, error, refetch } = useAccounts();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();

  // PMO は member-only（他ロールを含まない）アカウントのみ追加・編集・削除・impersonate 可能。
  // 付与可能なロールも member のみ。「member を含むか」ではなく「member 以外を含まないか」の
  // existential チェック（`['member', 'admin']` のような抜け穴を防ぐ）。
  // admin を同時保持するユーザーは admin が優先されこの制約を受けない
  // （`/auth/impersonate` の `isPmoCaller && !isAdminCaller` と同じ非対称仕様）。
  const isAdmin = (currentUser?.portalRoles ?? []).includes('admin');
  const isPmo = !isAdmin && (currentUser?.portalRoles ?? []).includes('pmo');
  const canActOnAccount = (account: Account) => !isPmo || isMemberOnlyRoleSet(account.portalRoles);
  const assignableRoles: PortalRole[] = isPmo
    ? ['member']
    : ['admin', 'group_manager', 'team_leader', 'member', 'architect', 'dle', 'tech_lead', 'pmo'];

  // モーダル状態
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState<string | null>(null);

  // 検索状態
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const { data: searchResults } = useUserSearch(searchQuery, !editingAccount);

  // フォーム状態（複数ロール同時選択可能）
  const [formRoles, setFormRoles] = useState<PortalRole[]>(['team_leader']);

  // モーダルを開く（追加モード）
  const handleAdd = useCallback(() => {
    setEditingAccount(null);
    setSelectedUser(null);
    setSearchQuery('');
    setFormRoles(isPmo ? ['member'] : ['team_leader']);
    setIsModalOpen(true);
  }, [isPmo]);

  // モーダルを開く（編集モード）
  const handleEdit = useCallback((account: Account) => {
    setEditingAccount(account);
    setSelectedUser(null);
    setSearchQuery('');
    setFormRoles(account.portalRoles);
    setIsModalOpen(true);
  }, []);

  // モーダルを閉じる
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingAccount(null);
    setSelectedUser(null);
    setSearchQuery('');
  }, []);

  // ユーザーを選択
  const handleSelectUser = useCallback(
    (user: UserSearchResult) => {
      setSelectedUser(user);
      setSearchQuery('');
      // PMO は member ロールしか付与できない。非 member の既存 portalRoles に倒すと
      // assignableRoles=['member'] に存在しない値になりチェックボックスが空欄表示 → 保存時に 403 になる。
      setFormRoles(
        isPmo ? ['member'] : user.portalRoles?.length ? user.portalRoles : ['team_leader'],
      );
    },
    [isPmo],
  );

  // ロールチェックボックスのトグル
  const toggleFormRole = useCallback((role: PortalRole) => {
    setFormRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }, []);

  // 保存処理
  const handleSave = useCallback(async () => {
    const email = editingAccount?.email ?? selectedUser?.email;
    if (!email) return;

    const updateData: UpdateAccountInput = {
      portalRoles: formRoles,
    };

    // 新規追加の場合は名前等も含める
    if (!editingAccount && selectedUser) {
      updateData.name = selectedUser.name;
      updateData.teamId = selectedUser.teamId;
      updateData.groupId = selectedUser.groupId;
    }

    try {
      await updateAccount.mutateAsync({ email, data: updateData });
      handleCloseModal();
    } catch (error) {
      // 失敗時は updateAccount.error 経由で UI に surface される。
      // ApiError は request() 側で 5xx/4xx を分類済みなので、ここで再 logError すると
      // 4xx (403 等) を error 扱いに昇格させてしまう → trace 用の logEvent に留める。
      // email は PII のため Faro ペイロードに含めない (docs/guidelines/security.md)。
      // 一方、ApiError 以外の本物の JS 例外 (TypeError 等) はサイレント化せず logError へ。
      if (error instanceof ApiError) {
        logEvent('update_account_modal_caught', { status: String(error.status) });
      } else {
        logError(error instanceof Error ? error : new Error(String(error)), {
          event: 'update_account_modal_unexpected',
        });
      }
    }
  }, [editingAccount, selectedUser, formRoles, updateAccount, handleCloseModal]);

  // Impersonate（別ユーザーとしてログイン）
  const handleImpersonate = useCallback(async (email: string, name: string) => {
    if (!confirm(`${name}（${email}）としてログインしますか？`)) return;
    try {
      await accountApi.impersonate(email);
      window.location.href = '/my-page';
    } catch {
      alert('ログインに失敗しました');
    }
  }, []);

  // 削除確認を開く
  const handleDeleteClick = useCallback((email: string) => {
    setDeleteConfirmEmail(email);
  }, []);

  // 削除確認を閉じる
  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmEmail(null);
  }, []);

  // 削除実行
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmEmail) return;
    await deleteAccount.mutateAsync(deleteConfirmEmail);
    setDeleteConfirmEmail(null);
  }, [deleteConfirmEmail, deleteAccount]);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
              {t('accounts.title')}
            </h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {t('accounts.description')}
            </p>
          </div>
          <div className="h-9 w-28 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        </div>
        <Card>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                      {t('accounts.fields.email')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                      {t('accounts.fields.name')}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                      {t('accounts.fields.portalRole')}
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                      {t('actions.edit')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map((i) => (
                    <tr key={i} className="border-b border-[var(--color-border)] last:border-b-0">
                      <td className="py-3 px-4">
                        <div className="h-4 w-40 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="h-4 w-20 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="h-5 w-16 rounded-full bg-[var(--color-bg-elevated)] animate-pulse" />
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="h-4 w-8 ml-auto rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const accounts = data?.accounts ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ページヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
            {t('accounts.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {t('accounts.description')}
          </p>
        </div>
        <Button onClick={handleAdd}>{t('accounts.add')}</Button>
      </div>

      {/* アカウント一覧 */}
      <Card>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                    {t('accounts.fields.email')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                    {t('accounts.fields.name')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                    {t('accounts.fields.portalRole')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-secondary)]">
                    {t('actions.edit')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-[var(--color-text-muted)]">
                      {t('accounts.noAccounts')}
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr
                      key={account.email}
                      className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                    >
                      <td className="py-3 px-4 text-sm text-[var(--color-text-primary)]">
                        {account.email}
                      </td>
                      <td className="py-3 px-4 text-sm text-[var(--color-text-primary)]">
                        {account.name}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {account.portalRoles.map((role) => (
                            <RoleBadge key={role} role={role} />
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        {canActOnAccount(account) && (
                          <button
                            onClick={() => {
                              handleEdit(account);
                            }}
                            className="px-3 py-1 text-sm text-[var(--color-accent-primary)] hover:bg-[var(--color-accent-muted)] rounded transition-colors"
                          >
                            {t('actions.edit')}
                          </button>
                        )}
                        {account.email !== currentUser?.email && canActOnAccount(account) && (
                          <>
                            <button
                              onClick={() => {
                                void handleImpersonate(account.email, account.name);
                              }}
                              className="px-3 py-1 text-sm text-[var(--color-warning)] hover:bg-yellow-50 rounded transition-colors"
                            >
                              ログイン
                            </button>
                            <button
                              onClick={() => {
                                handleDeleteClick(account.email);
                              }}
                              className="px-3 py-1 text-sm text-[var(--color-error)] hover:bg-red-50 rounded transition-colors"
                            >
                              {t('actions.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 追加/編集モーダル */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title={editingAccount ? t('accounts.edit') : t('accounts.add')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleCloseModal}>
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={() => {
                void handleSave();
              }}
              disabled={
                (!editingAccount && !selectedUser) ||
                formRoles.length === 0 ||
                updateAccount.isPending
              }
              loading={updateAccount.isPending}
            >
              {t('actions.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* ユーザー検索（追加モードのみ） */}
          {!editingAccount && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                {t('accounts.fields.email')}
              </label>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setSelectedUser(null);
                }}
                placeholder={t('accounts.searchPlaceholder')}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-primary)]"
                aria-label={t('accounts.fields.email')}
              />
              {selectedUser ? (
                <div className="rounded-lg border border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)] p-3">
                  <div className="text-sm font-medium text-[var(--color-text-primary)]">
                    {selectedUser.name}
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)]">
                    {selectedUser.email}
                    {selectedUser.portalRoles?.length
                      ? ` (${selectedUser.portalRoles.map((role) => t(`accounts.portalRoles.${role}`)).join(', ')})`
                      : ''}
                  </div>
                </div>
              ) : null}
              {searchQuery.length > 0 ? (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
                  {searchResults?.users.length ? (
                    searchResults.users.map((user) => (
                      <button
                        key={user.email}
                        type="button"
                        onClick={() => {
                          handleSelectUser(user);
                        }}
                        className="flex w-full items-center gap-3 border-b border-[var(--color-border)] px-3 py-2 text-left last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                      >
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-bg-elevated)] text-xs font-medium text-[var(--color-text-secondary)]">
                            {user.name.trim().slice(0, 2) || '?'}
                          </span>
                        )}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-[var(--color-text-primary)]">
                            {user.name}
                          </span>
                          <span className="block truncate text-xs text-[var(--color-text-muted)]">
                            {user.email}
                            {user.portalRoles?.length
                              ? ` (${user.portalRoles.map((role) => t(`accounts.portalRoles.${role}`)).join(', ')})`
                              : ''}
                          </span>
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-[var(--color-text-muted)]">
                      {searchQuery.length < 2
                        ? t('accounts.searchPlaceholder')
                        : t('accounts.noAccounts')}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* 編集モードの場合はユーザー情報を表示 */}
          {editingAccount && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
                {t('accounts.fields.email')}
              </label>
              <div className="p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                <div className="text-sm font-medium text-[var(--color-text-primary)]">
                  {editingAccount.name}
                </div>
                <div className="text-xs text-[var(--color-text-muted)]">{editingAccount.email}</div>
              </div>
            </div>
          )}

          {/* ロール選択 */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
              {t('accounts.fields.portalRole')}
            </label>
            <div className="space-y-2">
              {assignableRoles.map((role) => (
                <label
                  key={role}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formRoles.includes(role)
                      ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)]'
                      : 'border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="portalRoles"
                    value={role}
                    checked={formRoles.includes(role)}
                    onChange={() => {
                      toggleFormRole(role);
                    }}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-[var(--color-text-primary)]">
                      {t(`accounts.portalRoles.${role}`)}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {t(`accounts.portalRoleDescriptions.${role}`)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        open={!!deleteConfirmEmail}
        onClose={handleCancelDelete}
        title={t('accounts.delete')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleCancelDelete}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                void handleConfirmDelete();
              }}
              disabled={deleteAccount.isPending}
              loading={deleteAccount.isPending}
            >
              {t('actions.delete')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('accounts.confirmDelete')}
          </p>
          <p className="text-sm text-[var(--color-warning)]">{t('accounts.deleteWarning')}</p>
        </div>
      </Modal>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business ポータルロールを色分けバッジで表示
 * @graph-connects none
 */
function RoleBadge({ role }: { role: PortalRole }) {
  const { t } = useTranslation();

  const colorMap: Record<PortalRole, string> = {
    admin: 'bg-purple-100 text-purple-800',
    group_manager: 'bg-blue-100 text-blue-800',
    team_leader: 'bg-green-100 text-green-800',
    member: 'bg-gray-100 text-gray-600',
    architect: 'bg-amber-100 text-amber-800',
    dle: 'bg-teal-100 text-teal-800',
    tech_lead: 'bg-indigo-100 text-indigo-800',
    pmo: 'bg-rose-100 text-rose-800',
    // 'external' は productPortalRole 未設定者向けフォールバック。アカウント管理画面に
    // 表示されることは想定していないが、PortalRole の網羅性のために定義を提供する。
    external: 'bg-slate-100 text-slate-600',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[role]}`}
    >
      {t(`accounts.portalRoles.${role}`)}
    </span>
  );
}
